import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useConfig, useEstado } from '../api/hooks';
import { EstadoBadge } from '../components/EstadoBadge';
import { SiPuede } from '../components/SiPuede';
import type { Estado, FilaEstado } from '../types/api';
import { situacion } from '../utils/estadoTabla';
import { duracion, haceCuanto, numero, ventana } from '../utils/formato';

export function AvisoFrescura({ estado }: { estado: Estado | undefined }) {
  if (!estado) return null;
  if (!estado.recibido_at) {
    return <div className="aviso">Todavía no ha llegado el estado de Airflow: las columnas de estado salen vacías.</div>;
  }
  if (!estado.fresco) {
    return (
      <div className="aviso aviso-error" role="alert">
        El último estado de Airflow es de {haceCuanto(estado.recibido_at)}: ¿está parado el DAG <code>dwh_sincronizar</code>?
      </div>
    );
  }
  return null;
}

export function TablasPage() {
  const config = useConfig();
  const estado = useEstado();
  const [filtro, setFiltro] = useState('');
  const [soloProblemas, setSoloProblemas] = useState(false);

  const porTabla = useMemo(() => {
    const m = new Map<string, FilaEstado>();
    for (const f of estado.data?.tablas ?? []) m.set(f.tabla, f);
    return m;
  }, [estado.data]);

  if (config.isLoading) return <p className="cargando">Cargando la configuración…</p>;
  if (config.error) return <div className="aviso aviso-error">{config.error.message}</div>;
  const v = config.data!;
  const ahora = new Date();
  const filas = v.tablas
    .map((t) => ({ t, e: porTabla.get(t.tabla), s: situacion(t, porTabla.get(t.tabla), ahora) }))
    .filter(({ t }) => t.tabla.includes(filtro.trim().toUpperCase()))
    .filter(({ s }) => !soloProblemas || s === 'error' || s === 'retraso')
    .sort((a, b) => a.t.tabla.localeCompare(b.t.tabla));
  const conError = v.tablas.filter((t) => porTabla.get(t.tabla)?.ult_error).length;

  return (
    <>
      <div className="titulo">
        <h1>Tablas replicadas</h1>
        <SiPuede rol="dwh.editor">
          <Link className="boton boton-primario" to="/tablas/nueva">
            Añadir tabla
          </Link>
        </SiPuede>
      </div>
      <p className="subtitulo">
        Versión {v.id} <code>{v.version}</code> · {v.tablas.length} tablas ({v.tablas.filter((t) => t.activa).length} activas)
        {conError > 0 && <span className="badge badge-error">{conError} con error</span>}
      </p>
      <AvisoFrescura estado={estado.data} />
      <div className="filtros">
        <input type="search" placeholder="Buscar tabla…" value={filtro} onChange={(e) => setFiltro(e.target.value)} aria-label="Buscar tabla" />
        <label>
          <input type="checkbox" checked={soloProblemas} onChange={(e) => setSoloProblemas(e.target.checked)} /> Solo con error o retraso
        </label>
      </div>
      <table className="tabla">
        <thead>
          <tr>
            <th>Tabla</th>
            <th>Estado</th>
            <th>Altas / cambios / bajas</th>
            <th>Ventana</th>
            <th>Filtro</th>
            <th>Últimas altas</th>
            <th className="num">Filas</th>
          </tr>
        </thead>
        <tbody>
          {filas.map(({ t, e, s }) => (
            <tr key={t.tabla} className={t.activa ? undefined : 'inactiva'}>
              <td>
                <Link to={`/tablas/${t.tabla}`}>{t.tabla}</Link>
              </td>
              <td>
                <EstadoBadge situacion={s} titulo={e?.ult_error ?? undefined} />
              </td>
              <td>
                {duracion(t.frecuencia)} / {duracion(t.cambios)} / {duracion(t.bajas)}
              </td>
              <td>{ventana(t.ventana_ini, t.ventana_fin)}</td>
              <td>{t.filtro ? <code className="recorte">{t.filtro}</code> : '—'}</td>
              <td>{haceCuanto(e?.ult_altas, ahora)}</td>
              <td className="num">{numero(e?.filas)}</td>
            </tr>
          ))}
          {filas.length === 0 && (
            <tr>
              <td colSpan={7} className="vacio">
                Ninguna tabla coincide.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  );
}
